import { InfoOutlined, Timeline } from '@mui/icons-material';
import { Box, Chip, Divider, IconButton, List, ListItem, ListItemText, Paper, Tooltip, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { WidgetProps } from '../types/index.js';

type ImpactResult = {
  node: string;
  score: number;
  path?: string[];
};

export const ImpactGraphWidget: React.FC<WidgetProps> = ({ widget, onUpdateOutputs }) => {
  const impactResults = widget.inputs.find(i => i.id === 'impactResults')?.value as ImpactResult[] | undefined;
  const parameters = widget.inputs.find(i => i.id === 'parameters')?.value as { depth?: number; decay?: number; horizonDays?: number } | undefined;

  const [selected, setSelected] = useState<string | null>(null);

  const ranked = useMemo(() => {
    if (!impactResults) return [] as ImpactResult[];
    return [...impactResults].sort((a, b) => Math.abs(b.score) - Math.abs(a.score)).slice(0, 30);
  }, [impactResults]);

  const selectedResult = ranked.find(r => r.node === selected) || ranked[0];

  const handleSelect = (node: string) => {
    setSelected(node);
    const result = ranked.find(r => r.node === node);
    if (result && onUpdateOutputs) {
      onUpdateOutputs({ selectedTarget: result });
    }
  };

  if (!impactResults || impactResults.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Provide impactResults input to visualize nth-order impacts
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timeline fontSize="small" />
          <Typography variant="subtitle1">Impact Graph</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {parameters?.depth !== undefined && <Chip size="small" label={`Depth ${parameters.depth}`} />}
          {parameters?.decay !== undefined && <Chip size="small" label={`Decay ${parameters.decay}`} />}
          {parameters?.horizonDays !== undefined && <Chip size="small" label={`${parameters.horizonDays}d horizon`} />}
          <Tooltip title="Nth-order impact results with simple path hints">
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, p: 2, minHeight: 0 }}>
        {/* Ranked results */}
        <Paper sx={{ flex: 1, p: 1, minHeight: 0, overflow: 'auto' }} variant="outlined">
          <Typography variant="subtitle2" sx={{ px: 1, py: 1 }}>Top Affected</Typography>
          <List dense>
            {ranked.map((r) => (
              <ListItem key={r.node} selected={selected ? selected === r.node : ranked[0]?.node === r.node} button onClick={() => handleSelect(r.node)}>
                <ListItemText
                  primary={r.node}
                  secondary={`Score: ${r.score.toFixed(4)}`}
                />
                <Chip size="small" color={r.score > 0 ? 'success' : 'error'} label={r.score > 0 ? 'Positive' : 'Negative'} />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Path explanation */}
        <Paper sx={{ flex: 1.2, p: 2 }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            Why this target?
          </Typography>
          {selectedResult?.path && selectedResult.path.length > 1 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Path: {selectedResult.path.join(' → ')}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                The signal propagates along the path above with hop decay and time decay applied.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No path available. Run impact propagation with path capture to see explanations.
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};


